import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col } from 'reactstrap';
import {
    Typography,
    Switch,
    TextField,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from '@material-ui/core';
import { PrimaryButton } from '@/styles/blocks';
import { css } from '@emotion/react';
import useShippersAndCarriers from '@/hooks/useShippersAndCarriers';
import { featureFlagGroups } from './featureFlagsConstants';
import { useMutation, useQuery } from '@apollo/client';
import { UPDATE_FEATURE_FLAGS } from '@/graphql/mutations/users';
import { QUERY_BY_LEADER } from '@/graphql/queries/teammates';
import { ClientSelect } from '@/components/ShipmentForm/modals/InputFields';
import { UPDATE_CLIENT_BY_PK } from '@/graphql/mutations/clients';
import { QUERY_SUBREGION_TYPES } from '@/graphql/queries/subregions';

export default function AdminFeatureFlags(props) {
    const { uid, userObject, setUserObject, client } = props;
    const [loading, setLoading] = useState(false);
    const [teammates, setTeammates] = useState([]);
    const [_, carriers] = useShippersAndCarriers(client.test_acc);
    const [selectedCarrier, setSelectedCarrier] = useState('');
    const [rlxConfirmOpen, setRlxConfirmOpen] = useState(false);

    const updated = useMemo(() => {
        return {
            ...(client || {}),
            ...(selectedCarrier ? { auto_assigned_carrier: selectedCarrier } : {}),
        };
    }, [client, selectedCarrier]);

    useQuery(QUERY_BY_LEADER, {
        variables: { leader_id: uid },
        onCompleted: (data) => setTeammates(data.results.map((teammate) => teammate.teammate_id)),
    });

    const { data: types } = useQuery(QUERY_SUBREGION_TYPES, {});

    const [updateCircles] = useMutation(UPDATE_FEATURE_FLAGS, {
        onError: (e) => console.log(e),
        onCompleted: (data) => {
            setUserObject({
                ...userObject,
                circles: data.update_users.returning[0].circles,
            });
        },
    });

    const [updateClient] = useMutation(UPDATE_CLIENT_BY_PK, {
        onError: (e) => console.log(e),
        onCompleted: (data) => console.log(data),
    });

    const handleToggle = async (flag) => {
        setLoading(true);

        let newCircles = { ...(userObject?.circles || {}) };
        newCircles[flag] = !userObject?.circles?.[flag];

        let userList = [uid, ...teammates];

        updateCircles({
            variables: {
                user_list: userList,
                circles: newCircles,
            },
        });

        setLoading(false);
    };

    const RLX_FLAGS = {
        wms: true,
        'saas-v1': true,
        'asset-heavy': true,
        'accounting-v2': true,
        'ltl-marketplace': true,
        'overnight-routes': true,
        'plan-page-mapbox': true,
        'plan-page-received': true,
        'plan-reschedule-v1': true,
        'advanced-scheduling': true,
        'plan-page-load-board': true,
        'require-loading-scan': true,
        'plan-page-default-map': true,
        'plan-page-unscheduled': true,
        'require-delivery-scan': true,
        'custom-timeframes-beta': true,
        'prescheduled-highlight': true,
        'curbside-no-autoconfirm': true,
        'plan-page-order-pickups': true,
        'plan-page-shipper-rates': true,
        'customer-emails-with-sms': true,
        'hide-driver-phone-number': true,
        'plan-page-network-routes': true,
        'plan-page-recommendations': true,
        'disable-review-sms-messages': true,
        'disable-customer-availability': true,
        'sms-alert-next-stop-customers': true,
        'plan-page-show-rates': true,
    };

    const handleRlxFlags = () => {
        const newCircles = {
            ...(userObject?.circles || {}),
            ...RLX_FLAGS,
        };
        updateCircles({
            variables: {
                user_list: [uid, ...teammates],
                circles: newCircles,
            },
        });
        setRlxConfirmOpen(false);
    };

    const handleSelectCarrier = async (carrierId) => {
        setSelectedCarrier(carrierId);
        updateClient({
            variables: {
                client_id: uid,
                update: {
                    auto_assigned_carrier: carrierId,
                },
            },
        });
    };

    return (
        <Col
            css={css`
                padding-top: 45px;
            `}
        >
            <Row
                css={css`
                    align-items: center;
                    gap: 1rem;
                `}
            >
                <Typography variant="h4" className="mb-2">
                    Feature Flags
                </Typography>
            </Row>
            <Row
                css={css`
                    padding-left: 12px;
                    padding-top: 12px;
                    padding-bottom: 0px;
                `}
            >
                <PrimaryButton
                    size="small"
                    disabled={loading || !Object.keys(userObject).length}
                    onClick={() => setRlxConfirmOpen(true)}
                    css={css`
                        margin-bottom: 0.5rem;
                        width: auto;
                    `}
                >
                    Turn on RLX flags
                </PrimaryButton>

                <Dialog open={rlxConfirmOpen} onClose={() => setRlxConfirmOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Enable RLX Feature Flags?</DialogTitle>
                    <DialogContent>
                        <DialogContentText
                            css={css`
                                margin-bottom: 0.75rem;
                            `}
                        >
                            <strong>This action should only be performed for RLX Organization accounts.</strong>
                        </DialogContentText>
                        <DialogContentText>The following flags will be enabled for this account:</DialogContentText>
                        <ul
                            css={css`
                                margin-top: 0.5rem;
                                padding-left: 1.5rem;
                                column-count: 2;
                                column-gap: 1rem;
                            `}
                        >
                            {Object.keys(RLX_FLAGS).map((flag) => (
                                <li key={flag}>
                                    <Typography variant="body2">{flag}</Typography>
                                </li>
                            ))}
                        </ul>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRlxConfirmOpen(false)} color="default">
                            Cancel
                        </Button>
                        <Button onClick={handleRlxFlags} color="primary" variant="contained">
                            Enable Flags
                        </Button>
                    </DialogActions>
                </Dialog>
            </Row>
            {featureFlagGroups.map((group) => (
                <>
                    <Row
                        css={css`
                            margin-top: 2rem;
                            margin-bottom: 0.5rem;
                            border-bottom: 2px solid #e0e0e0;
                            padding-bottom: 0.25rem;
                        `}
                    >
                        <Typography
                            variant="h6"
                            css={css`
                                font-weight: 600;
                            `}
                        >
                            {group.label}
                        </Typography>
                    </Row>
                    {group.flags.map((flag) => (
                        <>
                            <Row>
                                <Col xs={4}>
                                    <Typography
                                        variant="subtitle2"
                                        css={css`
                                            align-self: center;
                                            font-size: 1.15rem;
                                        `}
                                    >
                                        {flag.name}
                                    </Typography>
                                </Col>
                                <Col xs={1}>
                                    <Switch
                                        color="primary"
                                        checked={!!userObject?.circles?.[flag.name]}
                                        onChange={() => handleToggle(flag.name)}
                                        disabled={loading || !Object.keys(userObject).length}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={12}>
                                    <Typography variant="subtitle2" className="ms-1">
                                        <i>{flag.description}</i>
                                    </Typography>
                                </Col>
                            </Row>
                            {flag.name === 'auto-assign-carrier' ? (
                                <Row>
                                    <Col xs={12}>
                                        <Typography variant="subtitle2" className="ms-1">
                                            <ClientSelect
                                                value={updated.auto_assigned_carrier || ''}
                                                options={carriers}
                                                onChange={(carrierId) => handleSelectCarrier(carrierId)}
                                                allowEmpty={true}
                                                type="carrier"
                                                css={css`
                                                    margin-left: 0.5rem;
                                                `}
                                            />
                                            <hr />
                                        </Typography>
                                    </Col>
                                </Row>
                            ) : null}
                        </>
                    ))}
                </>
            ))}
        </Col>
    );
}
